import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";
import { AppStrings } from "../../../utils/constants/AppStrings";

/** API contract: `target_position: -1` is the "pin to top" sentinel. */
export const VIEW_PLACEMENT_PIN_TO_TOP = -1;

/**
 * Backend `page_code` for the default storefront page (the Social Casino
 * lobby, served when no page is selected). The full set of pages is
 * fetched dynamically from `/pages` via {@link useAdminPages}; this constant
 * is only the fallback used when no page has been selected yet.
 */
export const DEFAULT_PAGE_CODE = "social";

/**
 * The `page_code` query param. The backend requires it on every views /
 * placements request to scope the result to a storefront page, so it is
 * always sent (defaulting to the Social Casino lobby).
 */
function pageCodeParam(pageCode: string = DEFAULT_PAGE_CODE): {
  page_code: string;
} {
  return { page_code: pageCode || DEFAULT_PAGE_CODE };
}

/**
 * Backend `/views` returns an open-ended list of slugs; today they are
 * `"carousels"` and `"shortcuts"` but more (e.g. `"mobile_drawer"`) can be
 * added server-side without a frontend release. Kept as `string` so new
 * values flow through automatically; UI labels / descriptions fall back to
 * a title-cased version of the slug when no explicit mapping exists.
 */
export type ViewKey = string;

export type AdminViewPlacementCollection = {
  code: string;
  name: string;
  name_translations?: Record<string, string>;
  kind: "games" | "providers";
  icon_url?: string | null;
  is_active?: boolean;
  member_count?: number;
  child_count?: number;
};

export type AdminViewPlacement = {
  collection: AdminViewPlacementCollection;
  display_order: number;
  is_active: boolean;
  is_pinned: boolean;
};

export type AdminViewPlacementsResult = {
  view: ViewKey;
  items: AdminViewPlacement[];
  total: number;
  active: number;
};

/**
 * A storefront page the operator can configure views for (e.g. Social Casino,
 * Live casino). `code` is the value passed as `page_code` on every views /
 * placements request; `is_default` marks the page served when none is picked.
 */
export type AdminPage = {
  code: string;
  name: string;
  name_translations?: Record<string, string>;
  description?: string | null;
  description_translations?: Record<string, string>;
  icon_url?: string | null;
  is_default: boolean;
  is_active: boolean;
};

function getInstance() {
  const token = JSON.parse(localStorage.getItem("token") ?? "null");
  return createPrivateAdminPanelInstance(token?.AccessToken);
}

function ensureSuccess(body: unknown, fallback: string): void {
  if (
    body &&
    typeof body === "object" &&
    (body as Record<string, unknown>).success === false
  ) {
    throw new Error(
      ((body as Record<string, unknown>).message as string) || fallback,
    );
  }
}

// WHY a runtime guard: these mappers rebuild typed DTOs from an untyped raw
// response (every field is normalized — String()/Boolean()/?? null), and
// `name_translations` is a new optional field the older BE may omit or send as
// null/[]. Only a plain object may reach the `{ locale: text }` lookup in
// resolveCurationText; anything else becomes undefined → canonical name.
function pickTranslationMap(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  return raw as Record<string, string>;
}

export async function fetchAdminPages(): Promise<AdminPage[]> {
  const instance = getInstance();
  const { data: body } = await instance.get(API_ROUTES.GET_ADMIN_PAGES);
  ensureSuccess(body, AppStrings.CASINO_CONFIG_VIEWS_FETCH_PAGES_FALLBACK);
  const items = body?.data?.items;
  if (!Array.isArray(items)) return [];
  return (items as Record<string, unknown>[]).map((entry) => ({
    code: String(entry.code ?? ""),
    name: String(entry.name ?? ""),
    name_translations: pickTranslationMap(entry.name_translations),
    description: (entry.description as string | null | undefined) ?? null,
    description_translations: pickTranslationMap(entry.description_translations),
    icon_url: (entry.icon_url as string | null | undefined) ?? null,
    is_default: Boolean(entry.is_default),
    is_active: Boolean(entry.is_active),
  }));
}

const ADMIN_PAGES_QUERY_KEY = ["casinoConfiguration", "views", "pages"];

export function useAdminPages(enabled = true) {
  return useQuery({
    queryKey: ADMIN_PAGES_QUERY_KEY,
    queryFn: fetchAdminPages,
    staleTime: 60_000,
    enabled,
  });
}

export type CreateAdminPageInput = {
  code: string;
  name: string;
  description?: string;
};

export type UpdateAdminPageInput = {
  name?: string;
  description?: string;
  is_active?: boolean;
};

export type UpdateAdminPageVars = UpdateAdminPageInput & { code: string };

export function useCreateAdminPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAdminPageInput) => {
      const instance = getInstance();
      const { data } = await instance.post(API_ROUTES.CREATE_ADMIN_PAGE, input);
      ensureSuccess(data, AppStrings.CASINO_CONFIG_PAGES_CREATE_FALLBACK);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_PAGES_QUERY_KEY });
    },
  });
}

export function useUpdateAdminPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ code, ...patch }: UpdateAdminPageVars) => {
      const instance = getInstance();
      const url = API_ROUTES.UPDATE_ADMIN_PAGE.replace(
        "{code}",
        encodeURIComponent(code),
      );
      const { data } = await instance.patch(url, patch);
      ensureSuccess(data, AppStrings.CASINO_CONFIG_PAGES_UPDATE_FALLBACK);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_PAGES_QUERY_KEY });
    },
  });
}

export function useDeleteAdminPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) => {
      const instance = getInstance();
      const url = API_ROUTES.DELETE_ADMIN_PAGE.replace(
        "{code}",
        encodeURIComponent(code),
      );
      const { data } = await instance.delete(url);
      ensureSuccess(data, AppStrings.CASINO_CONFIG_PAGES_DELETE_FALLBACK);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_PAGES_QUERY_KEY });
    },
  });
}

export async function fetchAdminViews(
  pageCode?: string,
): Promise<ViewKey[]> {
  const instance = getInstance();
  const { data: body } = await instance.get(API_ROUTES.GET_ADMIN_VIEWS, {
    params: pageCodeParam(pageCode),
  });
  ensureSuccess(body, AppStrings.CASINO_CONFIG_VIEWS_FETCH_VIEWS_FALLBACK);
  const items = body?.data?.items;
  return Array.isArray(items) ? (items as ViewKey[]) : [];
}

export function useAdminViews(pageCode: string = DEFAULT_PAGE_CODE) {
  return useQuery({
    queryKey: ["casinoConfiguration", "views", "list", pageCode],
    queryFn: () => fetchAdminViews(pageCode),
    // Wait for a resolved page code before fetching so we never hit the API
    // with an empty/placeholder page (avoids a wrong-page round-trip on mount).
    enabled: Boolean(pageCode),
    staleTime: 60_000,
  });
}

export async function fetchAdminViewPlacements(
  view: ViewKey,
  search?: string,
  pageCode?: string,
): Promise<AdminViewPlacementsResult> {
  const instance = getInstance();
  const url = API_ROUTES.GET_ADMIN_VIEW_PLACEMENTS.replace(
    "{view}",
    encodeURIComponent(view),
  );
  const params: Record<string, unknown> = { ...pageCodeParam(pageCode) };
  if (search) params.search = search;
  const { data: body } = await instance.get(url, { params });
  ensureSuccess(
    body,
    AppStrings.CASINO_CONFIG_VIEWS_FETCH_PLACEMENTS_FALLBACK,
  );

  const data = (body?.data ?? {}) as Record<string, unknown>;
  const rawItems = Array.isArray(data.items)
    ? (data.items as Record<string, unknown>[])
    : [];

  const items: AdminViewPlacement[] = rawItems.map((entry) => {
    const collection = (entry.collection ?? {}) as Record<string, unknown>;
    return {
      collection: {
        code: String(collection.code ?? ""),
        name: String(collection.name ?? ""),
        name_translations: pickTranslationMap(collection.name_translations),
        kind: ((collection.kind as string) === "providers"
          ? "providers"
          : "games") as "games" | "providers",
        icon_url: (collection.icon_url as string | null | undefined) ?? null,
        is_active: collection.is_active as boolean | undefined,
        member_count: collection.member_count as number | undefined,
        child_count: collection.child_count as number | undefined,
      },
      display_order:
        typeof entry.display_order === "number" ? entry.display_order : 0,
      is_active: Boolean(entry.is_active),
      is_pinned: Boolean(entry.is_pinned),
    };
  });

  const total =
    typeof data.total === "number" ? (data.total as number) : items.length;
  const active =
    typeof data.active === "number"
      ? (data.active as number)
      : items.filter((p) => p.is_active).length;

  return {
    view: (data.view as ViewKey) ?? view,
    items,
    total,
    active,
  };
}

export function useAdminViewPlacements(
  view: ViewKey | null,
  search?: string,
  pageCode: string = DEFAULT_PAGE_CODE,
) {
  return useQuery({
    queryKey: [
      "casinoConfiguration",
      "views",
      "placements",
      pageCode,
      view ?? "_disabled",
      search ?? "",
    ],
    queryFn: () =>
      fetchAdminViewPlacements(view as ViewKey, search || undefined, pageCode),
    enabled: Boolean(view),
    staleTime: 0,
  });
}

export function useAddAdminViewPlacements(
  view: ViewKey,
  pageCode: string = DEFAULT_PAGE_CODE,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (collectionCodes: string[]) => {
      const instance = getInstance();
      const url = API_ROUTES.ADD_ADMIN_VIEW_PLACEMENTS.replace(
        "{view}",
        encodeURIComponent(view),
      );
      const body = { collection_codes: collectionCodes };
      const { data } = await instance.post(url, body, {
        params: pageCodeParam(pageCode),
      });
      ensureSuccess(
        data,
        AppStrings.CASINO_CONFIG_VIEWS_ADD_PLACEMENTS_FALLBACK,
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "casinoConfiguration",
          "views",
          "placements",
          pageCode,
          view,
        ],
      });
    },
  });
}

export type MoveViewPlacementInput = {
  collection_code: string;
  target_position: number;
};

export function useMoveAdminViewPlacement(
  view: ViewKey,
  pageCode: string = DEFAULT_PAGE_CODE,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: MoveViewPlacementInput) => {
      const instance = getInstance();
      const url = API_ROUTES.MOVE_ADMIN_VIEW_PLACEMENT.replace(
        "{view}",
        encodeURIComponent(view),
      );
      const { data } = await instance.post(url, input, {
        params: pageCodeParam(pageCode),
      });
      ensureSuccess(
        data,
        AppStrings.CASINO_CONFIG_VIEWS_MOVE_PLACEMENT_FALLBACK,
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "casinoConfiguration",
          "views",
          "placements",
          pageCode,
          view,
        ],
      });
    },
  });
}

export type PatchViewPlacementInput = {
  collection_code: string;
  is_active?: boolean;
};

export function usePatchAdminViewPlacement(
  view: ViewKey,
  pageCode: string = DEFAULT_PAGE_CODE,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      collection_code,
      ...patch
    }: PatchViewPlacementInput) => {
      const instance = getInstance();
      const url = API_ROUTES.PATCH_ADMIN_VIEW_PLACEMENT.replace(
        "{view}",
        encodeURIComponent(view),
      ).replace("{code}", encodeURIComponent(collection_code));
      const { data } = await instance.patch(url, patch, {
        params: pageCodeParam(pageCode),
      });
      ensureSuccess(
        data,
        AppStrings.CASINO_CONFIG_VIEWS_PATCH_PLACEMENT_FALLBACK,
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "casinoConfiguration",
          "views",
          "placements",
          pageCode,
          view,
        ],
      });
    },
  });
}

export function useDeleteAdminViewPlacement(
  view: ViewKey,
  pageCode: string = DEFAULT_PAGE_CODE,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (collectionCode: string) => {
      const instance = getInstance();
      const url = API_ROUTES.DELETE_ADMIN_VIEW_PLACEMENT.replace(
        "{view}",
        encodeURIComponent(view),
      ).replace("{code}", encodeURIComponent(collectionCode));
      const { data } = await instance.delete(url, {
        params: pageCodeParam(pageCode),
      });
      ensureSuccess(
        data,
        AppStrings.CASINO_CONFIG_VIEWS_DELETE_PLACEMENT_FALLBACK,
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "casinoConfiguration",
          "views",
          "placements",
          pageCode,
          view,
        ],
      });
    },
  });
}
